import { ArrowUpRight, TrendingUp, DollarSign, Home } from "lucide-react";

export default function CaseStudy() {
    return (
        <section id="case-study" className="relative py-24 sm:py-32 border-t border-white/10 overflow-hidden">
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-brand/[0.05] to-transparent" />
            <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full bg-brand/12 blur-[180px]" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14">
                    <div className="md:col-span-8">
                        <div className="eyebrow mb-4 flex items-center gap-3">
                            <span className="inline-block w-10 h-[2px] bg-brand" /> Recent case study
                        </div>
                        <h2 className="font-display font-medium tracking-[-0.04em] text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.02]">
                            <span className="text-brand">3 deals</span> in 3 weeks.<br />
                            <span className="text-white/40">CPL:</span> AED&nbsp;14.
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <a
                            href="https://instagram.com/elrashedybasma"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-white/60 hover:text-brand transition-colors border-b border-white/20 hover:border-brand pb-1"
                        >
                            @elrashedybasma &rarr;
                        </a>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-5 bg-[#141414] border border-white/10 p-8 sm:p-10">
                        <div className="text-xs font-mono uppercase tracking-[0.3em] text-brand mb-6">Client</div>
                        <div className="flex items-start gap-4">
                            <div className="w-14 h-14 rounded-full bg-brand flex items-center justify-center font-display font-semibold text-white text-xl shrink-0">
                                BM
                            </div>
                            <div>
                                <div className="font-display font-semibold text-2xl text-white tracking-[-0.02em]">Basma Mohamed</div>
                                <div className="text-white/60 text-sm">@elrashedybasma</div>
                                <div className="mt-2 text-xs font-mono uppercase tracking-widest text-white/40">
                                    Real Estate &middot; Dubai
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 pt-8 border-t border-white/10">
                            <div className="text-xs font-mono uppercase tracking-widest text-white/40 mb-3">The Play</div>
                            <ul className="space-y-2 text-white/80 text-sm">
                                <li className="flex gap-3"><span className="text-brand">01</span> Meta Ads, targeted UAE + GCC buyers</li>
                                <li className="flex gap-3"><span className="text-brand">02</span> Custom creative + Arabic-first hooks</li>
                                <li className="flex gap-3"><span className="text-brand">03</span> Weekly A/B testing on copy & visuals</li>
                                <li className="flex gap-3"><span className="text-brand">04</span> WhatsApp handoff for instant follow-up</li>
                            </ul>
                        </div>
                    </div>

                    <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="sm:col-span-2 bg-brand relative overflow-hidden p-8 sm:p-10 min-h-[220px]">
                            <div className="absolute -bottom-10 -right-10 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
                            <div className="relative">
                                <div className="text-xs font-mono uppercase tracking-[0.3em] text-white/80">Cost Per Lead</div>
                                <div className="numeric-display mt-4 font-semibold text-white leading-[0.9] text-7xl sm:text-9xl">
                                    AED&nbsp;14
                                </div>
                                <div className="mt-4 text-white/90 text-base font-medium">
                                    Off-plan property leads &middot; Meta Ads
                                </div>
                            </div>
                        </div>

                        <div className="bg-[#141414] border border-white/10 p-6 sm:p-8 flex flex-col justify-between min-h-[180px] group hover:border-brand/60 transition-colors">
                            <TrendingUp className="w-6 h-6 text-brand" />
                            <div>
                                <div className="numeric-display font-semibold text-5xl text-white">3<span className="text-brand">×</span></div>
                                <div className="mt-2 text-white/60 text-sm">Deals closed in 3 weeks</div>
                            </div>
                        </div>

                        <div className="bg-[#141414] border border-white/10 p-6 sm:p-8 flex flex-col justify-between min-h-[180px] group hover:border-brand/60 transition-colors">
                            <DollarSign className="w-6 h-6 text-brand" />
                            <div>
                                <div className="numeric-display font-semibold text-5xl text-white">6M<span className="text-brand">+</span></div>
                                <div className="mt-2 text-white/60 text-sm">In pipeline generated</div>
                            </div>
                        </div>

                        <div className="sm:col-span-2 border border-brand/40 p-6 sm:p-8 relative overflow-hidden bg-[#0d0d0f]">
                            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-brand/10 via-transparent to-transparent" />
                            <div className="relative flex items-center justify-between gap-4 flex-wrap">
                                <p className="text-white text-lg sm:text-xl font-medium tracking-[-0.02em] max-w-xl">
                                    Want the same numbers for your listings?
                                </p>
                                <a
                                    href="#packages"
                                    className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white text-sm font-semibold px-5 py-2.5 transition-colors"
                                    data-testid="case-study-cta"
                                >
                                    Get started
                                    <ArrowUpRight className="w-4 h-4" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
